import { z } from 'zod'
import { BotSessionState } from '../../enums/bot-session-state.enum'

export const openOrderSchema = z.object({
  price: z.number(),
  lots: z.number().int(),
  direction: z.enum(['buy', 'sell']),
})

export type OpenOrderDto = z.infer<typeof openOrderSchema>

export const botSessionStateSchema = z.object({
  instrumentUid: z.string(),
  ticker: z.string().nullable(),
  state: z.nativeEnum(BotSessionState),
  errorReason: z.string().nullable(),
  peakPriceSeen: z.number(),
  armThreshold: z.number(),
  localLow: z.number().nullable(),
  localLowTimestamp: z.coerce.date().nullable(),
  confirmationTicks: z.number().int(),
  confirmationTicksTarget: z.number().int(),
  confirmationSecondsTarget: z.number().int(),
  lastOrderPrice: z.number().nullable(),
  // when true, the cooldown anchor (lastOrderPrice) is retained but gated off → drop-mode
  // arming; the UI offers Restore instead of Clear.
  cooldownCleared: z.boolean(),
  currentPrice: z.number().nullable(),
  dropFromPeak: z.number().nullable(),
  percentUntilArm: z.number().nullable(),
  deviation: z.number().nullable(),
  // signed money needed on this position to drive the displayed deviation to exactly 0%:
  // positive → buy/add, negative → sell/remove. solved against the same moving denominator
  // the live deviation uses, so adding this amount makes the shown deviation hit 0. null when
  // no live price / no plan inputs / target ≥ 100% (unsolvable).
  moneyToZeroDeviation: z.number().nullable(),
  holdingQty: z.number().nullable(),
  lotSize: z.number().int().nullable(),
  averagePrice: z.number().nullable(),
  sessionOpenPrice: z.number().nullable(),
  openOrders: z.array(openOrderSchema),
  effectiveDropPercent: z.number(),
  effectiveShiftPercent: z.number(),
  // within-day accumulation depth e = max(0, fillsToday - 1); resets at 00:00 UTC. drives BOTH
  // ramps: shift × (1 + e·shiftChange) and lots × (1 + e·lotChange). advances on every fill even
  // when neither ramp is configured (both default to 0), so a non-zero e alone does not mean
  // anything is actually escalating.
  escalationSteps: z.number().int(),
  // the pre-escalation shift %, i.e. what effectiveShiftPercent would be at e = 0. paired with
  // effectiveShiftPercent (which is ALREADY escalated) it gives the ramp's before → after.
  baseShiftPercent: z.number(),
  // lots the next buy would send, after the maxLotsPerBuy clamp.
  nextBuyLots: z.number().int(),
  // true when the lot ramp has run up into the maxLotsPerBuy ceiling, so further escalation adds
  // no lots (only the shift keeps widening). requires lotChange > 0 — with the ramp switched off
  // lots never grow, and a lotsPerStep >= maxLotsPerBuy config would otherwise read as "capped"
  // from the very first buy.
  nextBuyLotsCapped: z.boolean(),
  // transient manual pins overriding the disproportion interpolation; null when the
  // effective value is computed. cleared at daily reset or on the next manual change.
  manualDropPercent: z.number().nullable(),
  manualShiftPercent: z.number().nullable(),
  // absolute manual arm-threshold override (top precedence); null when not set.
  manualThresholdPrice: z.number().nullable(),
  // which branch produced armThreshold: 'manual' (manualThresholdPrice), 'drop' (peak ×
  // drop%), 'shift' (last fill × shift%), or 'maxBuy' (the drop/shift threshold sat above
  // maxBuyPrice, so the ceiling caps it). a manual threshold is never capped.
  thresholdMode: z.enum(['manual', 'drop', 'shift', 'maxBuy']),
  // the price armThreshold is built from: manualThresholdPrice in manual mode,
  // peakPriceSeen in drop mode, lastOrderPrice in shift mode, maxBuyPrice in maxBuy mode.
  thresholdAnchorPrice: z.number(),
  // in maxBuy mode, the drop/shift threshold the ceiling capped — so the UI can still show the
  // peak / cooldown context behind a capped arm price; null in every other mode.
  uncappedThreshold: z
    .object({
      mode: z.enum(['drop', 'shift']),
      anchorPrice: z.number(),
      threshold: z.number(),
    })
    .nullable(),
  lastBuyAt: z.coerce.date().nullable(),
})

export type BotSessionStateDto = z.infer<typeof botSessionStateSchema>

// control frames sent on the per-account bot SSE stream alongside the BotSessionStateDto
// ticks. `orders-changed` tells the frontend to refetch orders + available cash without
// waiting for the next tick; `instrument-offline` tells it to drop a single instrument's
// live state immediately (e.g. the bot/instrument was stopped) so the UI flips to offline
// at once instead of lingering on the last frame until a refetch or SSE reconnect.
export type BotStreamControlFrame =
  | { kind: 'orders-changed' }
  | { kind: 'instrument-offline'; instrumentUid: string }
